import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Sparkles, Search, ChefHat, CalendarDays, BookHeart, Image, MessageCircle, Users, Wheat, Send, Bell, Play } from "lucide-react";

// Card grande en el home que explica todo lo que se puede hacer.
// Colapsable — se guarda estado en localStorage para que la primera vez
// abra desplegada y después el usuario decida.
const LS_KEY = "recetario_guide_open";

const SECTIONS = [
  {
    id: "buscar",
    icon: Search,
    title: "Búsqueda inteligente",
    body: "Busca por nombre, ingrediente o país. Entiende sinónimos chilenos (palta = aguacate, choclo = maíz, camote = batata) y plurales. Usa los chips de filtro para acotar por categoría, origen o «Sin gluten».",
  },
  {
    id: "cook",
    icon: ChefHat,
    title: "Modo Cocinar con la voz de Papi",
    body: "Abre cualquier receta y toca «▶ Cocinar». Vas paso a paso y Papi te lee cada instrucción con su voz clonada. Arriba a la derecha hay un botón de 🔊 auto-play: si lo activas, los audios de cada paso arrancan solos sin apretar nada — cocina con las manos ocupadas.",
  },
  {
    id: "semanal",
    icon: CalendarDays,
    title: "Menú Semanal (nuevo)",
    body: "Ve a «Menú Semanal» desde el home. Elige si quieres una semana Normal, Light o Especial y Papi te arma 7 platos variados. Puedes cambiar cualquier día (con un dado o eligiendo tú), reordenar y activar avisos diarios para que Papi te recuerde por notificación qué toca cocinar. La lista de compras sale sola, agrupada por sección y con cantidades sumadas.",
    link: { to: "/menu-semanal", label: "Abrir Menú Semanal" },
  },
  {
    id: "menus",
    icon: BookHeart,
    title: "Mis Menús (para fiestas y eventos)",
    body: "Crea menús para Pesaj, un cumpleaños, un fin de semana largo. Agrega tantas recetas como quieras y la app te arma la lista de compras clasificada. Cada miembro tiene sus propios menús.",
    link: { to: "/menus", label: "Ver Mis Menús" },
  },
  {
    id: "galeria",
    icon: Image,
    title: "Galería de platos",
    body: "Cuando cocines algo, saca una foto y súbela. Los demás miembros pueden dejar comentarios y así vamos armando la memoria visual del recetario.",
    link: { to: "/galeria", label: "Ir a la Galería" },
  },
  {
    id: "papi",
    icon: MessageCircle,
    title: "Pregúntale a Papi",
    body: "Un botón flotante 💬 que abre un chat con Papi. Le puedes preguntar dudas de cocina, sugerencias de vino, cómo cambiar un ingrediente, o cualquier duda de la vida — te responde con su voz.",
  },
  {
    id: "porciones",
    icon: Users,
    title: "Escalar porciones",
    body: "Dentro de cada receta hay una calculadora de porciones. Puedes decir «somos 8 en vez de 4» o «tengo 750 g de pollo en vez de 1 kg» y las cantidades de todos los ingredientes se recalculan automáticamente.",
  },
  {
    id: "kosher",
    icon: Wheat,
    title: "Modo Kosher",
    body: "En el login puedes activar «modo kosher» para tu sesión. La app oculta recetas con cerdo/mariscos y muestra tips kosher (mezcla lácteo/carne, sustituciones, etc.).",
  },
  {
    id: "sugerir",
    icon: Send,
    title: "Sugerir una receta (nuevo)",
    body: "¿Se te ocurre un plato que Papi debería agregar? Manda tu sugerencia con el nombre y por qué te gustaría. Papi la recibe en su panel de admin, la revisa y decide cuándo publicarla.",
    link: { to: "/sugerir", label: "Mandar una sugerencia" },
  },
  {
    id: "push",
    icon: Bell,
    title: "Notificaciones push",
    body: "Activa las notificaciones para recibir avisos de cumpleaños familiares, cuando Papi sube una receta nueva y — si activaste el Menú Semanal — un aviso diario que te dice qué toca cocinar.",
  },
];

export default function FeaturesGuide() {
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw === null ? true : raw === "1";
    } catch { return true; }
  });
  const [expanded, setExpanded] = useState({});

  const toggleAll = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(LS_KEY, next ? "1" : "0"); } catch { /* silent */ }
  };
  const toggleSec = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <section data-testid="features-guide" className="mb-8 rounded-2xl border-2 border-amber-deep/30 bg-gradient-to-br from-amber-light/30 via-cream to-paper-warm overflow-hidden">
      <button
        data-testid="features-guide-toggle"
        onClick={toggleAll}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-amber-light/20 transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-deep/20 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-amber-deep" />
          </div>
          <div>
            <p className="eyebrow text-[10px]">Guía rápida</p>
            <h2 className="font-display italic text-cocoa text-xl leading-tight">Todo lo que puedes hacer aquí</h2>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-cocoa" /> : <ChevronDown className="w-5 h-5 text-cocoa" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-2">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isOpen = !!expanded[s.id];
            return (
              <div key={s.id} className="bg-cream/80 rounded-xl border border-line overflow-hidden">
                <button
                  data-testid={`feature-${s.id}-toggle`}
                  onClick={() => toggleSec(s.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-light/20 transition"
                >
                  <Icon className="w-4 h-4 text-amber-deep shrink-0" />
                  <span className="font-serif text-cocoa font-semibold text-sm flex-1">{s.title}</span>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-cocoa-soft" /> : <ChevronDown className="w-4 h-4 text-cocoa-soft" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-line/60">
                    <p className="font-serif text-cocoa-soft text-sm italic leading-relaxed mb-3">{s.body}</p>
                    {s.link && (
                      <Link
                        data-testid={`feature-${s.id}-link`}
                        to={s.link.to}
                        className="inline-flex items-center gap-1 text-xs font-serif italic text-amber-deep hover:underline"
                      >
                        <Play className="w-3 h-3" /> {s.link.label}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
